import { forwardRef, useState } from 'react';
import { Select, SelectProps } from 'antd';
import type { SizeType } from 'antd/lib/config-provider/SizeContext';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { RefSelectProps } from 'antd/es/select';
import useProjectSelect from './useProjectSelect';
import { useFilteredProjectOptions } from './useFilteredProjectOptions';

interface ProjectSelectProps {
  isCreateModal?: boolean;
  open?: boolean;
  value?: string;
  className?: string;
  size?: SizeType;
  initialProjects?: Record<string, any>[];
  allowClear?: boolean;
  withPersonal?: boolean;
  onlyRetainer?: boolean;
  onlyActive?: boolean;
  bordered?: boolean;
  multiple?: boolean;
  disableProjects?: boolean;
  onChange?: SelectProps<string>['onChange'];
  onDropdownVisibleChange?: SelectProps<string>['onDropdownVisibleChange'];
}

const ProjectSelect = forwardRef<RefSelectProps, ProjectSelectProps>(function ProjectSelect(
  {
    isCreateModal,
    open,
    value,
    className,
    size,
    allowClear,
    withPersonal,
    onlyRetainer,
    onlyActive,
    bordered,
    multiple,
    initialProjects,
    onChange,
    onDropdownVisibleChange,
    disableProjects = false,
  },
  ref,
) {
  const { data, loading } = useSelector((store: RootState) => store.projectSelect);

  const [searchValue, setSearchValue] = useState('');

  const projects: any = data ?? initialProjects;

  const { options, onDropdownVisibleChangeHandler, filterOption } = useProjectSelect({
    projects,
    withPersonal,
    onlyRetainer,
    onlyActive,
  });

  const filteredOptions = useFilteredProjectOptions({
    options,
    searchValue,
    filterOption,
    isCreateModal,
  });

  const finalOptionsBase = isCreateModal ? filteredOptions : filteredOptions.filter((opt) => !opt.extra.project);

  const finalOptions = disableProjects
    ? finalOptionsBase.map((opt) => ({
        ...opt,
        businessName: opt.extra.customer ? opt.extra.customer.business_name : opt.display,
        disabled: !opt.extra.project,
      }))
    : finalOptionsBase.map((opt) => ({
        ...opt,
        businessName: opt.extra.customer ? opt.extra.customer.business_name : opt.display,
      }));

  return (
    <Select
      ref={ref}
      open={open}
      value={value}
      className={className}
      dropdownClassName="no-padding select-plan-request"
      placeholder="Select a plan"
      optionLabelProp="businessName"
      mode={multiple ? 'multiple' : undefined}
      maxTagCount="responsive"
      menuItemSelectedIcon={null}
      options={finalOptions}
      loading={loading}
      size={size}
      allowClear={allowClear}
      bordered={bordered}
      autoFocus
      showSearch
      filterOption={false}
      onSearch={(v) => setSearchValue(v)}
      onChange={onChange}
      onDropdownVisibleChange={(newOpen) => {
        onDropdownVisibleChange?.(newOpen);
        onDropdownVisibleChangeHandler(newOpen);
      }}
    />
  );
});

export default ProjectSelect;
